import { useRef, useState } from "react";
import { Check, NumberField, RefsPicker, Select, TextArea, TextField } from "../fields";
import type { B, EditorProps } from "./types";

/** What a clip can be: video files, and GIFs (which play like a muted, looping clip). */
export const CLIP_TYPES = "video/mp4,video/webm,image/gif";
export const isClip = (contentType: string) => contentType.startsWith("video/") || contentType === "image/gif";

export function VideoEditor({ value: b, onChange, deck, assets, upload, setLocalErrors }: EditorProps<B<"video">>) {
  const set = (patch: Partial<B<"video">>) => onChange({ ...b, ...patch });
  const [uploading, setUploading] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const clips = assets.filter((a) => isClip(a.contentType));
  const current = assets.find((a) => a.id === b.assetId);
  const gif = current?.contentType === "image/gif";

  const setRange = (patch: Partial<Pick<B<"video">, "start" | "end">>) => {
    const next = { ...b, ...patch };
    setLocalErrors(
      next.start !== undefined && next.end !== undefined && next.end <= next.start
        ? ["The clip has to end after it starts."]
        : [],
    );
    onChange(next);
  };

  return (
    <>
      <TextField label="Title" max={80} value={b.title} onChange={(title) => set({ title })} />
      <TextArea label="Lead" max={240} value={b.lead} onChange={(lead) => set({ lead: lead || undefined })} />
      <div className="grid-2">
        <Select
          label="Clip"
          value={b.assetId ?? ""}
          options={[{ value: "", label: "No clip yet" }, ...clips.map((a) => ({ value: a.id, label: a.filename }))]}
          onChange={(id) => {
            const a = assets.find((x) => x.id === id);
            set({ assetId: id || null, ...(a && { filename: a.filename }) });
          }}
        />
        <div className="field">
          <span className="field-label">Upload</span>
          <button type="button" className="s-btn" disabled={uploading} onClick={() => file.current?.click()}>
            {uploading ? "Uploading…" : "Upload clip"}
          </button>
          <input
            ref={file}
            type="file"
            accept={CLIP_TYPES}
            hidden
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              setUploading(true);
              const a = await upload(f);
              setUploading(false);
              if (a) set({ assetId: a.id, filename: a.filename });
            }}
          />
        </div>
      </div>
      <span className="field-hint">MP4, WebM or GIF, up to 50 MB. It plays on the projector; phones show the title.</span>
      <TextField
        label="Caption"
        max={200}
        value={b.caption ?? ""}
        onChange={(caption) => set({ caption: caption || undefined })}
      />
      {gif ? (
        <span className="field-hint">A GIF always plays from the start, loops and has no sound.</span>
      ) : (
        <>
          <div className="grid-2">
            <NumberField
              label="Start at (seconds)"
              optional
              min={0}
              step={0.5}
              value={b.start}
              onChange={(start) => setRange({ start })}
            />
            <NumberField
              label="Stop at (seconds)"
              optional
              min={0}
              step={0.5}
              value={b.end}
              onChange={(end) => setRange({ end })}
            />
          </div>
          <div className="row-gap">
            <Check label="Loop" checked={b.loop} onChange={(loop) => set({ loop })} />
            <Check label="Muted" checked={b.muted} onChange={(muted) => set({ muted })} />
          </div>
        </>
      )}
      <RefsPicker references={deck.references} value={b.refs} onChange={(refs) => set({ refs })} />
    </>
  );
}
